/**
 * The removal flow: ask, confirm, then one request per file with each
 * refusal reported by name.
 *
 * Its own hook so the pane holds the screen and this holds the rule. A
 * refusal of one file leaves the others removed and names the one that
 * stayed; the rows come off the cache as each delete lands, so the list
 * never shows a file as gone before the server has said so.
 */

import { useState } from "react";
import type { FileResponse } from "@patent-studio/api-types";
import { refusalDetail } from "@/services/api-client";
import { useDeleteFile } from "@/hooks/use-files";
import type { DeleteRequest } from "./DeleteConfirmation";

function deleteFailure(error: unknown, name: string): string {
  return refusalDetail(error, `${name} could not be removed.`);
}

export function useDeleteFlow(taskId: string, files: readonly FileResponse[]) {
  const remove = useDeleteFile(taskId);
  const [request, setRequest] = useState<DeleteRequest | null>(null);
  const [error, setError] = useState<string | null>(null);

  const nameOf = (fileId: string) =>
    files.find((f) => f.file_id === fileId)?.original_filename ?? fileId;

  const confirm = async (confirmed: DeleteRequest) => {
    setRequest(null);
    setError(null);
    const ids =
      confirmed.kind === "one" ? [confirmed.fileId] : confirmed.fileIds;
    // Sent together: the deletes are independent and each is a targeted
    // write, so one refusal leaves the rest removed and is named on its own.
    const outcomes = await Promise.allSettled(
      ids.map((fileId) => remove.mutateAsync(fileId)),
    );
    const refused = outcomes.flatMap((outcome, at) =>
      outcome.status === "rejected"
        ? [deleteFailure(outcome.reason, nameOf(ids[at]))]
        : [],
    );
    if (refused.length > 0) setError(refused.join(" "));
  };

  return {
    request,
    error,
    askOne: (file: FileResponse) => {
      setError(null);
      setRequest({
        kind: "one",
        fileId: file.file_id,
        name: file.original_filename,
      });
    },
    askMany: (fileIds: string[]) => {
      setError(null);
      setRequest({ kind: "many", fileIds });
    },
    cancel: () => setRequest(null),
    confirm: (confirmed: DeleteRequest) => void confirm(confirmed),
  };
}
